// src/pages/PrivacyPage.tsx

import { FC } from 'react';
import styles from '../css/PrivacyPage.module.css';
import { motion, Variants } from 'framer-motion';
import { Lock, Eye, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

const PrivacyPage: FC = () => {
    const lastUpdated = "November 22, 2025";

    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } 
    };
    
    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
    };

    return (
        <div className={styles.privacyPage}>
            
            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            className={styles.heroWrapper}
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Data Governance
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Privacy & <br/>
                                <span className={styles.titleHighlight}>Security Protocols.</span>
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                We treat data integrity as a foundational asset. 
                                This document outlines our rigorous standards for collection, encryption, and usage.
                            </motion.p>

                            <motion.div variants={itemVariants} className={styles.metaInfo}>
                                <span className={styles.dateLabel}>Last Updated: {lastUpdated}</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. POLICY CONTENT */}
            <AnimatedSection className={styles.contentSection}>
                <div className={styles.container}>
                    <div className={styles.documentLayout}>
                        
                        {/* Sidebar Navigation */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarSticky}>
                                <div className={styles.iconBlock}>
                                    <Lock size={24} strokeWidth={1.5} />
                                </div>
                                <h3 className={styles.sidebarTitle}>Policy Index</h3>
                                <ul className={styles.tocList}>
                                    <li>1. Information Collection</li>
                                    <li>2. Usage Protocols</li>
                                    <li>3. Data Sovereignty</li>
                                    <li>4. User Rights</li>
                                    <li>5. Contact DPO</li>
                                </ul>
                                <Link to="/" className={styles.backLink}>
                                    <ArrowLeft size={14} /> Return Home
                                </Link>
                            </div>
                        </aside>

                        {/* Main Text Content */}
                        <div className={styles.legalTextWrapper}>
                            
                            <div className={styles.legalSection}>
                                <h2>1. Information Collection</h2>
                                <p>
                                    OTA TECH Inc. collects information necessary to provide our ERP and FinTech services. This includes:
                                </p>
                                <ul>
                                    <li><strong>Operational Data:</strong> Transaction logs, inventory metrics, and financial entries processed through OTA ERP.</li>
                                    <li><strong>Identity Data:</strong> Corporate entity details, authorized user credentials, and KYC/KYB documentation required for financial compliance.</li>
                                    <li><strong>Telemetry:</strong> Anonymized usage data to optimize system performance and latency.</li>
                                </ul>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>2. Usage Protocols</h2>
                                <p>
                                    We utilize data strictly for the purpose of service delivery, security monitoring, and algorithmic optimization.
                                </p>
                                <div className={styles.highlightBox}>
                                    <Eye size={20} className={styles.highlightIcon} />
                                    <p>
                                        <strong>No Third-Party Sales:</strong> We do not sell, rent, or trade your operational data to third-party advertisers. Your data is your asset.
                                    </p>
                                </div>
                                <p>
                                    Data may be processed by our proprietary AI models ("ANNA AI") solely to generate insights specific to your organization. These models are isolated to prevent cross-tenant data leakage.
                                </p>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>3. Data Sovereignty & Security</h2>
                                <p>
                                    We employ enterprise-grade security measures to protect data both at rest and in transit.
                                </p>
                                <ul>
                                    <li><strong>Encryption:</strong> AES-256 encryption for database storage and TLS 1.3 for data in transit.</li>
                                    <li><strong>Infrastructure:</strong> Hosted on AWS utilizing Virtual Private Cloud (VPC) architecture for network isolation.</li>
                                    <li><strong>Access Control:</strong> Strict Role-Based Access Control (RBAC) and Multi-Factor Authentication (MFA) enforcement.</li>
                                </ul>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>4. Your Rights</h2>
                                <p>
                                    Depending on your jurisdiction (including GDPR for European entities and local data protection laws in Ghana and the US), you have specific rights regarding your data:
                                </p>
                                <ul>
                                    <li>The right to access and export your operational data.</li>
                                    <li>The right to request deletion of data upon contract termination.</li>
                                    <li>The right to rectification of inaccurate records.</li>
                                </ul>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>5. Contact Data Protection Officer</h2>
                                <p>
                                    For specific inquiries regarding data privacy, compliance audits, or security reports, please contact our Data Protection Office.
                                </p>
                                <div className={styles.contactCard}>
                                    <ShieldCheck size={24} className={styles.contactIcon} />
                                    <div>
                                        <h4>Data Protection Office</h4>
                                        <p>Email: <strong>privacy@otatech.org</strong></p>
                                        <p>Ref: DPO Inquiry</p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default PrivacyPage;